/**
 * Login Error Classifier
 *
 * Classifies login errors into distinct categories for better UX:
 * - network: Connection issues (offline, timeout, DNS failure)
 * - auth: Invalid credentials (wrong password, user not found)
 * - server: Backend issues (503, 500, rate limiting)
 * - unknown: Unclassified errors
 *
 * Based on best practices from OWASP, Stripe, and Microsoft auth flows.
 */

import { AuthError } from '@supabase/supabase-js';
import { authLogger } from './logging';

export type LoginErrorType = 'network' | 'auth' | 'server' | 'timeout' | 'unknown';

export interface LoginErrorClassification {
  /** Error category for UI treatment */
  type: LoginErrorType;
  /** Whether user can meaningfully retry this error */
  isRetryable: boolean;
  /** Translation key for user-facing message */
  messageKey: string;
  /** Fallback message if translation unavailable */
  fallbackMessage: string;
  /** Suggested action for UI */
  suggestedAction: 'retry' | 'check_credentials' | 'wait' | 'contact_support';
  /** Technical details for logging (not shown to user) */
  technicalMessage: string;
}

/**
 * Network connection information for diagnostic logging
 */
export interface NetworkDiagnostics {
  /** navigator.onLine state */
  online: boolean;
  /** Connection effective type (4g, 3g, 2g, slow-2g) if available */
  effectiveType?: string;
  /** Downlink speed in Mbps if available */
  downlink?: number;
  /** Round-trip time in ms if available */
  rtt?: number;
  /** Timestamp of check */
  timestamp: string;
}

/**
 * Get current network diagnostics for logging
 */
export function getNetworkDiagnostics(): NetworkDiagnostics {
  // Access the Network Information API if available (not in all browsers)
  const nav = navigator as typeof navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
  };
  const connection = nav.connection;

  const diagnostics: NetworkDiagnostics = {
    online: navigator.onLine,
    timestamp: new Date().toISOString(),
  };

  // Only add optional properties if they have values
  if (connection?.effectiveType) {
    diagnostics.effectiveType = connection.effectiveType;
  }
  if (connection?.downlink !== undefined) {
    diagnostics.downlink = connection.downlink;
  }
  if (connection?.rtt !== undefined) {
    diagnostics.rtt = connection.rtt;
  }

  return diagnostics;
}

/**
 * Quick check if user appears to be offline
 * Note: navigator.onLine only indicates LAN connection, not internet access
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

/**
 * Classify a login error for appropriate UI treatment
 *
 * @param error - The error from login attempt
 * @returns Classification with type, message, and suggested action
 */
export function classifyLoginError(error: unknown): LoginErrorClassification {
  // Log diagnostics for debugging
  const diagnostics = getNetworkDiagnostics();
  authLogger.debug('[LoginErrorClassifier] Classifying error', {
    errorType: error?.constructor?.name,
    errorMessage: error instanceof Error ? error.message : String(error),
    networkDiagnostics: diagnostics,
  });

  // Check for offline state first
  if (!navigator.onLine) {
    return {
      type: 'network',
      isRetryable: true,
      messageKey: 'auth.login.offlineError',
      fallbackMessage: 'You appear to be offline. Please check your internet connection.',
      suggestedAction: 'retry',
      technicalMessage: 'navigator.onLine returned false',
    };
  }

  // Handle Supabase AuthError
  if (error instanceof AuthError || (error as { __isAuthError?: boolean }).__isAuthError) {
    return classifyAuthError(error as AuthError);
  }

  // Handle generic errors
  if (error instanceof Error) {
    return classifyGenericError(error);
  }

  // Handle error-like objects
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as { message?: string; status?: number; code?: string };
    if (errorObj.message || errorObj.status || errorObj.code) {
      return classifyErrorObject(errorObj);
    }
  }

  // Unknown error type
  return {
    type: 'unknown',
    isRetryable: true,
    messageKey: 'auth.login.unknownError',
    fallbackMessage: 'Something went wrong. Please try again.',
    suggestedAction: 'retry',
    technicalMessage: `Unknown error type: ${String(error)}`,
  };
}

/**
 * Classify Supabase AuthError
 */
function classifyAuthError(error: AuthError): LoginErrorClassification {
  const message = (error.message || '').toLowerCase();
  const status = (error as { status?: number }).status || 0;

  // Invalid credentials - most common auth error
  if (
    message.includes('invalid login credentials') ||
    message.includes('invalid email or password') ||
    message.includes('email not confirmed') ||
    status === 400
  ) {
    return {
      type: 'auth',
      isRetryable: false,
      messageKey: 'auth.login.invalidCredentials',
      fallbackMessage: 'Invalid email or password. Please check and try again.',
      suggestedAction: 'check_credentials',
      technicalMessage: `Auth error: ${error.message}`,
    };
  }

  // User not found or banned
  if (message.includes('user not found') || message.includes('user banned') || status === 403) {
    return {
      type: 'auth',
      isRetryable: false,
      messageKey: 'auth.login.accountIssue',
      fallbackMessage: 'There is an issue with your account. Please contact support.',
      suggestedAction: 'contact_support',
      technicalMessage: `Account error: ${error.message}`,
    };
  }

  // Rate limiting
  if (message.includes('rate limit') || message.includes('too many requests') || status === 429) {
    return {
      type: 'server',
      isRetryable: true,
      messageKey: 'auth.login.rateLimited',
      fallbackMessage: 'Too many login attempts. Please wait a moment before trying again.',
      suggestedAction: 'wait',
      technicalMessage: `Rate limited: ${error.message}`,
    };
  }

  // Server errors
  if (status >= 500) {
    return {
      type: 'server',
      isRetryable: true,
      messageKey: 'auth.login.serverError',
      fallbackMessage: 'Our servers are temporarily unavailable. Please try again.',
      suggestedAction: 'retry',
      technicalMessage: `Server error ${status}: ${error.message}`,
    };
  }

  // Default to auth error for other AuthError cases
  return {
    type: 'auth',
    isRetryable: false,
    messageKey: 'auth.login.invalidCredentials',
    fallbackMessage: 'Invalid email or password.',
    suggestedAction: 'check_credentials',
    technicalMessage: `Auth error: ${error.message}`,
  };
}

/**
 * Classify generic Error objects
 */
function classifyGenericError(error: Error): LoginErrorClassification {
  const message = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();

  // Timeout errors (AbortError from AbortController)
  if (name === 'aborterror' || message.includes('abort') || message.includes('timeout')) {
    return {
      type: 'timeout',
      isRetryable: true,
      messageKey: 'auth.login.timeoutError',
      fallbackMessage: 'Connection timed out. Please check your connection and try again.',
      suggestedAction: 'retry',
      technicalMessage: `Timeout: ${error.message}`,
    };
  }

  // Network/fetch errors
  if (
    name === 'typeerror' ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    message.includes('dns') ||
    message.includes('ssl') ||
    message.includes('certificate')
  ) {
    return {
      type: 'network',
      isRetryable: true,
      messageKey: 'auth.login.networkError',
      fallbackMessage: 'Unable to connect. Please check your internet connection and try again.',
      suggestedAction: 'retry',
      technicalMessage: `Network error: ${error.message}`,
    };
  }

  // CORS errors (often indicate network/proxy issues)
  if (message.includes('cors') || message.includes('access-control')) {
    return {
      type: 'network',
      isRetryable: true,
      messageKey: 'auth.login.networkError',
      fallbackMessage: 'Unable to connect. Please check your internet connection.',
      suggestedAction: 'retry',
      technicalMessage: `CORS error: ${error.message}`,
    };
  }

  // Default to unknown
  return {
    type: 'unknown',
    isRetryable: true,
    messageKey: 'auth.login.unknownError',
    fallbackMessage: 'Something went wrong. Please try again.',
    suggestedAction: 'retry',
    technicalMessage: `Error: ${error.message}`,
  };
}

/**
 * Classify error-like objects with message/status/code
 */
function classifyErrorObject(error: {
  message?: string;
  status?: number;
  code?: string;
}): LoginErrorClassification {
  const message = (error.message || '').toLowerCase();
  const status = error.status || 0;

  // HTTP status-based classification
  if (status === 401 || status === 400) {
    return {
      type: 'auth',
      isRetryable: false,
      messageKey: 'auth.login.invalidCredentials',
      fallbackMessage: 'Invalid email or password.',
      suggestedAction: 'check_credentials',
      technicalMessage: `HTTP ${status}: ${error.message}`,
    };
  }

  if (status === 403) {
    return {
      type: 'auth',
      isRetryable: false,
      messageKey: 'auth.login.accountIssue',
      fallbackMessage: 'There is an issue with your account.',
      suggestedAction: 'contact_support',
      technicalMessage: `HTTP ${status}: ${error.message}`,
    };
  }

  if (status === 429) {
    return {
      type: 'server',
      isRetryable: true,
      messageKey: 'auth.login.rateLimited',
      fallbackMessage: 'Too many attempts. Please wait a moment.',
      suggestedAction: 'wait',
      technicalMessage: `HTTP ${status}: ${error.message}`,
    };
  }

  if (status >= 500 || status === 503) {
    return {
      type: 'server',
      isRetryable: true,
      messageKey: 'auth.login.serverError',
      fallbackMessage: 'Our servers are temporarily unavailable.',
      suggestedAction: 'retry',
      technicalMessage: `HTTP ${status}: ${error.message}`,
    };
  }

  // Message-based classification for status 0 or missing
  if (status === 0 || message.includes('network') || message.includes('fetch')) {
    return {
      type: 'network',
      isRetryable: true,
      messageKey: 'auth.login.networkError',
      fallbackMessage: 'Unable to connect. Please check your internet connection.',
      suggestedAction: 'retry',
      technicalMessage: `Network error: ${error.message}`,
    };
  }

  return {
    type: 'unknown',
    isRetryable: true,
    messageKey: 'auth.login.unknownError',
    fallbackMessage: 'Something went wrong. Please try again.',
    suggestedAction: 'retry',
    technicalMessage: `Error object: ${JSON.stringify(error)}`,
  };
}

/**
 * Log a login attempt for diagnostics (call this before and after login)
 *
 * @param phase - 'before' or 'after' login attempt
 * @param email - User email (will be partially masked)
 * @param result - Result of login attempt (for 'after' phase)
 */
export function logLoginAttempt(
  phase: 'before' | 'after',
  email: string,
  result?: { success: boolean; errorType?: LoginErrorType; latencyMs?: number }
): void {
  const maskedEmail = maskEmail(email);
  const diagnostics = getNetworkDiagnostics();

  if (phase === 'before') {
    authLogger.debug('[LoginAttempt] Starting login', {
      email: maskedEmail,
      networkState: diagnostics,
    });
  } else {
    authLogger.debug('[LoginAttempt] Login completed', {
      email: maskedEmail,
      success: result?.success,
      errorType: result?.errorType,
      latencyMs: result?.latencyMs,
      networkState: diagnostics,
    });
  }
}

/**
 * Mask email for logging (privacy)
 * example@domain.com -> e***e@d***n.com
 */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return '***@***.***';

  const maskedLocal =
    local.length > 2 ? `${local[0]}***${local[local.length - 1]}` : '***';

  const domainParts = domain.split('.');
  const maskedDomain = domainParts
    .map((part) =>
      part.length > 2 ? `${part[0]}***${part[part.length - 1]}` : '***'
    )
    .join('.');

  return `${maskedLocal}@${maskedDomain}`;
}
